"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.extractMetricValue = extractMetricValue;
exports.isDataLakeFormat = isDataLakeFormat;
exports.transformFrequentSearchesWithoutResult = transformFrequentSearchesWithoutResult;
exports.transformKeyInsightMetric = transformKeyInsightMetric;
exports.transformTopSearchResults = transformTopSearchResults;
exports.transformTopSearches = transformTopSearches;

/**
 * Search Analytics Response Transformer
 *
 * This module handles the transformation of Search Analytics API responses
 * to a normalized format that can be consumed by the UI components.
 *
 * The API can return two different response formats:
 * 1. Legacy/Algolia Format: Uses `data.searches`, `data.hits`, `data.count`, `data.rate`
 * 2. Data Lake Format: Uses `data.summary.data`, `data.summary.total`, `data.summary.rate`
 *    - Identified by `data.metadata.source === 'data_lake'`
 *
 * IMPORTANT: The `user_count` metric ALWAYS uses Algolia format, even when Data Lake is enabled.
 *
 * The transformer detects the format automatically and normalizes to a consistent structure.
 */

/**
 * Normalized search item for table display
 */

/**
 * Normalized search result item for table display
 */

/**
 * Normalized key insight metric data
 */

/**
 * Data Lake response format - Metadata structure
 */

/**
 * Data Lake response format - Summary structure
 */

/**
 * Data Lake response format - Full structure
 */

/**
 * Legacy/Algolia response format
 */

/**
 * API response data structure (can be either format)
 */

/**
 * Checks if the response is in Data Lake format
 *
 * According to the syncsearch API documentation, the correct way to detect raw data
 * Data Lake format is by checking `metadata.source === 'data_lake'`
 *
 * @param data - Raw API response data
 * @returns true if the response is from Data Lake
 */
function isDataLakeFormat(data) {
  var _metadata;

  if (!data) return false; // Check for the definitive Data Lake indicator
  // Using type-safe optional chaining to access nested property

  return (data === null || data === void 0 ? void 0 : (_metadata = data.metadata) === null || _metadata === void 0 ? void 0 : _metadata.source) === 'data_lake';
}
/**
 * Safely convert a value to a number
 * @param value - Any value to convert
 * @param defaultValue - Default value if conversion fails
 * @returns The numeric value or default
 */


function safeNumber(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : 0;
  if (value === null || value === undefined) return defaultValue;
  var num = Number(value);
  return isNaN(num) ? defaultValue : num;
}
/**
 * Safely convert a value to a string
 * @param value - Any value to convert
 * @param defaultValue - Default value if conversion fails
 * @returns The string value or default
 */


function safeString(value) {
  var defaultValue = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : '';
  if (value === null || value === undefined) return defaultValue;
  return String(value);
}
/**
 * Transform API response for Top Searches table
 *
 * Handles both Algolia and Data Lake response formats.
 *
 * @param data - Raw API response data
 * @returns Normalized array of search items
 */


function transformTopSearches(data) {
  if (!data) return [];

  try {
    if (isDataLakeFormat(data)) {
      var _data$summary;

      // Data Lake format: data.summary.data[].{search, search_count, hits_number_count}
      var items = (_data$summary = data.summary) === null || _data$summary === void 0 ? void 0 : _data$summary.data;

      if (!items || !Array.isArray(items)) {
        console.warn('Data Lake response missing summary.data for top_searches');
        return [];
      }

      return items.map(function (item) {
        return {
          search: safeString(item.search),
          count: safeNumber(item.search_count),
          nbHits: safeNumber(item.hits_number_count)
        };
      });
    } // Legacy/Algolia format: data.searches[].{search, count, nbHits}
    // Access searches property - it exists on LegacyResponse


    var searches = 'searches' in data ? data.searches : undefined;

    if (!searches || !Array.isArray(searches)) {
      return [];
    }

    return searches.map(function (item) {
      return {
        search: safeString(item.search),
        count: safeNumber(item.count),
        nbHits: safeNumber(item.nbHits)
      };
    });
  } catch (error) {
    console.error('Error transforming top searches:', error);
    return [];
  }
}
/**
 * Transform API response for Frequent Searches Without Results table
 *
 * Handles both Algolia and Data Lake response formats.
 *
 * @param data - Raw API response data
 * @returns Normalized array of search items with filter counts
 */


function transformFrequentSearchesWithoutResult(data) {
  if (!data) return [];

  try {
    if (isDataLakeFormat(data)) {
      var _data$summary2;

      // Data Lake format: data.summary.data[].{search, searches_with_no_results_count, searches_with_filters_with_no_results_count}
      var items = (_data$summary2 = data.summary) === null || _data$summary2 === void 0 ? void 0 : _data$summary2.data;

      if (!items || !Array.isArray(items)) {
        console.warn('Data Lake response missing summary.data for frequent_searches_without_result');
        return [];
      }

      return items.map(function (item) {
        var search = safeString(item.search);
        var count = safeNumber(item.searches_with_no_results_count);
        var withFilterCount = safeNumber(item.searches_with_filters_with_no_results_count);
        return {
          search: search,
          count: count,
          withFilterCount: withFilterCount,
          ui_title: "".concat(search, " of which ").concat(withFilterCount, " filtered")
        };
      });
    } // Legacy/Algolia format: data.searches[].{search, count, withFilterCount, ui_title}


    var searches = 'searches' in data ? data.searches : undefined;

    if (!searches || !Array.isArray(searches)) {
      return [];
    }

    return searches.map(function (item) {
      return {
        search: safeString(item.search),
        count: safeNumber(item.count),
        withFilterCount: safeNumber(item.withFilterCount),
        ui_title: safeString(item.ui_title)
      };
    });
  } catch (error) {
    console.error('Error transforming frequent searches without result:', error);
    return [];
  }
}
/**
 * Transform API response for Top Search Results table
 *
 * Handles both Algolia and Data Lake response formats.
 *
 * @param data - Raw API response data
 * @returns Normalized array of search result items
 */


function transformTopSearchResults(data) {
  if (!data) return [];

  try {
    if (isDataLakeFormat(data)) {
      var _data$summary3;

      // Data Lake format: data.summary.data[].{hit, results_count, title?}
      var items = (_data$summary3 = data.summary) === null || _data$summary3 === void 0 ? void 0 : _data$summary3.data;

      if (!items || !Array.isArray(items)) {
        console.warn('Data Lake response missing summary.data for top_search_results');
        return [];
      }

      return items.map(function (item) {
        return {
          title: safeString(item.title) || safeString(item.hit),
          count: safeNumber(item.results_count),
          hit: safeString(item.hit)
        };
      });
    } // Legacy/Algolia format: data.hits[].{hit, title, count}


    var hits = 'hits' in data ? data.hits : undefined;

    if (!hits || !Array.isArray(hits)) {
      return [];
    }

    return hits.map(function (item) {
      return {
        title: safeString(item.title),
        count: safeNumber(item.count),
        hit: safeString(item.hit)
      };
    });
  } catch (error) {
    console.error('Error transforming top search results:', error);
    return [];
  }
}
/**
 * Transform API response for Key Insight metrics (counts and rates)
 *
 * Handles both Algolia and Data Lake response formats.
 * Note: user_count ALWAYS uses Algolia format even when Data Lake is enabled.
 *
 * @param data - Raw API response data
 * @returns Normalized metric data with count or rate
 */


function transformKeyInsightMetric(data) {
  if (!data) return {
    count: 0,
    rate: 0
  };

  try {
    if (isDataLakeFormat(data)) {
      // Data Lake format: data.summary.{total, rate}
      var summary = data.summary;

      if (!summary) {
        console.warn('Data Lake response missing summary for key insight metric');
        return {
          count: 0,
          rate: 0
        };
      }

      return {
        count: safeNumber(summary.total),
        rate: safeNumber(summary.rate)
      };
    } // Legacy/Algolia format: data.{count, rate}
    // This is also the format for user_count which ALWAYS uses Algolia


    var count = 'count' in data ? data.count : undefined;
    var rate = 'rate' in data ? data.rate : undefined;
    return {
      count: safeNumber(count),
      rate: safeNumber(rate)
    };
  } catch (error) {
    console.error('Error transforming key insight metric:', error);
    return {
      count: 0,
      rate: 0
    };
  }
}
/**
 * Extract the metric value from normalized data
 *
 * @param normalizedData - Normalized metric data
 * @param isPercentage - Whether to return rate (true) or count (false)
 * @returns The metric value (rate * 100 for percentages)
 */


function extractMetricValue(normalizedData, isPercentage) {
  if (isPercentage) {
    return safeNumber(normalizedData.rate) * 100;
  }

  return safeNumber(normalizedData.count);
}